// === Application State ===
/* eslint-disable prefer-const -- 状态模块中的 let 导出是有意的，表示可变状态 */
import type { SessionMeta, SessionSnapshot, RuntimeInfo, AttachmentItem, ActiveSessionLoadState } from './types';
import { AGENT_LABELS, DEFAULT_AGENT, INPUT_MAX_HEIGHT_FALLBACK } from './constants';

// --- Raw state variables (module-private) ---
let ws: WebSocket | null = null;
let authToken: string | null = localStorage.getItem('webcoding-token');
export let pendingAuthPassword: string | null = null;
export let pendingPasswordChangeValue: string | null = null;
let currentSessionId: string | null = null;
let sessions: SessionMeta[] = [];
let sessionCache: Map<string, SessionSnapshot> = new Map();
let isGenerating = false;
let reconnectAttempts = 0;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
export let pendingText = '';
export let renderTimer: ReturnType<typeof setTimeout> | null = null;
let activeToolCalls: Map<string, any> = new Map();
export let cmdMenuIndex = -1;
export let cmdMenuDelegated = false;
let currentMode = 'yolo';
let currentModel = '';
let currentActiveRuntime: RuntimeInfo | null = null;
let currentRuntimeCount = 0;
const savedAgent = localStorage.getItem('webcoding-agent');
export let selectedAgent: string = AGENT_LABELS[savedAgent ?? ''] ? (savedAgent as string) : DEFAULT_AGENT;
let currentAgent: string = selectedAgent;
export let modelConfigCache: any = null;
export let codexConfigCache: any = null;
let loadedHistorySessionId: string | null = null;
let activeSessionLoad: ActiveSessionLoadState | null = null;
let currentHistoryTotal = 0;
let currentHistoryBuffered = 0;
let currentHistoryPending = false;
let currentHistoryLoading = false;
let currentHistoryRequestSeq = 0;
export let sidebarSwipe: any = null;
let pendingAttachments: AttachmentItem[] = [];
let uploadingAttachments: AttachmentItem[] = [];
let currentCwd: string | null = null;
let currentSessionRunning = false;
export let skipDeleteConfirm = localStorage.getItem('webcoding-skip-delete-confirm') === '1';
let pendingInitialSessionLoad = false;
export let projects: any[] = [];
export let collapsedProjects: Set<string> = new Set();
export let sidebarResizeState: any = null;
export let gitPanelResizeState: any = null;
export let lastVisibilityResyncAt = 0;
export let localIdCounter = 0;
export let cachedInputMaxHeight = INPUT_MAX_HEIGHT_FALLBACK;
export const toastQueue: any[] = [];
export let activeToast: any = null;
export let pendingProjectSaveCallback: ((projects: any[]) => void) | null = null;
export let gitState: any = {
  cwd: null,
  loading: false,
  lastError: '',
  status: null,
  logEntries: [],
  branchEntries: [],
  panelOpen: false,
  activePanelView: 'status',
  panelDiffContent: '',
  panelDiffTitle: '',
  panelLogEntries: [],
  filesExpanded: false,
  collapsedTreeNodes: [],
};

// --- Setters for reassignable exports ---
export function setPendingAuthPassword(v: string | null): void { pendingAuthPassword = v; }
export function setPendingPasswordChangeValue(v: string | null): void { pendingPasswordChangeValue = v; }
export function setModelConfigCache(v: any): void { modelConfigCache = v; }
export function setCodexConfigCache(v: any): void { codexConfigCache = v; }
export function setProjects(v: any[]): void { projects = v; }
export function setPendingProjectSaveCallback(v: ((p: any[]) => void) | null): void { pendingProjectSaveCallback = v; }

// --- Grouped state accessors (proxy pattern) ---

export const connectionState = {
  get ws() { return ws; },
  set ws(value: WebSocket | null) { ws = value; },
  get reconnectAttempts() { return reconnectAttempts; },
  set reconnectAttempts(value: number) { reconnectAttempts = value; },
  get reconnectTimer() { return reconnectTimer; },
  set reconnectTimer(value: ReturnType<typeof setTimeout> | null) { reconnectTimer = value; },
  get pendingInitialSessionLoad() { return pendingInitialSessionLoad; },
  set pendingInitialSessionLoad(value: boolean) { pendingInitialSessionLoad = value; },
  get authToken() { return authToken; },
  set authToken(value: string | null) { authToken = value; },
};

export const sessionState = {
  get currentSessionId() { return currentSessionId; },
  set currentSessionId(value: string | null) { currentSessionId = value; },
  get currentAgent() { return currentAgent; },
  set currentAgent(value: string) { currentAgent = value; },
  get currentMode() { return currentMode; },
  set currentMode(value: string) { currentMode = value; },
  get currentModel() { return currentModel; },
  set currentModel(value: string) { currentModel = value; },
  get currentActiveRuntime() { return currentActiveRuntime; },
  set currentActiveRuntime(value: RuntimeInfo | null) { currentActiveRuntime = value; },
  get currentRuntimeCount() { return currentRuntimeCount; },
  set currentRuntimeCount(value: number) { currentRuntimeCount = value; },
  get sessions() { return sessions; },
  set sessions(value: SessionMeta[]) { sessions = value; },
  get sessionCache() { return sessionCache; },
  set sessionCache(value: Map<string, SessionSnapshot>) { sessionCache = value; },
  get loadedHistorySessionId() { return loadedHistorySessionId; },
  set loadedHistorySessionId(value: string | null) { loadedHistorySessionId = value; },
  get activeSessionLoad() { return activeSessionLoad; },
  set activeSessionLoad(value: typeof activeSessionLoad) { activeSessionLoad = value; },
  get currentHistoryTotal() { return currentHistoryTotal; },
  set currentHistoryTotal(value: number) { currentHistoryTotal = value; },
  get currentHistoryBuffered() { return currentHistoryBuffered; },
  set currentHistoryBuffered(value: number) { currentHistoryBuffered = value; },
  get currentHistoryPending() { return currentHistoryPending; },
  set currentHistoryPending(value: boolean) { currentHistoryPending = value; },
  get currentHistoryLoading() { return currentHistoryLoading; },
  set currentHistoryLoading(value: boolean) { currentHistoryLoading = value; },
  get currentHistoryRequestSeq() { return currentHistoryRequestSeq; },
  set currentHistoryRequestSeq(value: number) { currentHistoryRequestSeq = value; },
  get currentCwd() { return currentCwd; },
  set currentCwd(value: string | null) { currentCwd = value; },
  get currentSessionRunning() { return currentSessionRunning; },
  set currentSessionRunning(value: boolean) { currentSessionRunning = value; },
};

export const composeState = {
  get isGenerating() { return isGenerating; },
  set isGenerating(value: boolean) { isGenerating = value; },
  get pendingText() { return pendingText; },
  set pendingText(value: string) { pendingText = value; },
  get activeToolCalls() { return activeToolCalls; },
  set activeToolCalls(value: Map<string, any>) { activeToolCalls = value; },
  get pendingAttachments() { return pendingAttachments; },
  set pendingAttachments(value: AttachmentItem[]) { pendingAttachments = value; },
  get uploadingAttachments() { return uploadingAttachments; },
  set uploadingAttachments(value: AttachmentItem[]) { uploadingAttachments = value; },
};
